// ========================================================================
// 💉 SERVICIO DE VACUNACIÓN - vaccinationService.js
// ========================================================================

import { 
  API_BASE_VACCINATION_URL,
  API_VACCINATION_LIST,
  API_VACCINATION_DETAILS,
  API_VACCINATION_CREATE,
  API_VACCINATION_UPDATE,
  API_VACCINATION_DELETE,
  API_VACCINATION_BULK_ACTION,
  API_VACCINATION_SEND_REMINDER
} from '../../../helpers/urlHelper';

// ========================================================================
// 🔧 CONFIGURACIÓN BASE
// ========================================================================

const BASE_URL = API_BASE_VACCINATION_URL;

// Headers por defecto
const getHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
});

// Manejo de errores
const handleResponse = async (response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP Error: ${response.status}`);
  }
  return response.json();
};

// ========================================================================
// 📋 OPERACIONES CRUD BÁSICAS
// ========================================================================

/**
 * Obtener todas las vacunaciones
 * @param {Object} params - Parámetros de consulta (page, limit, search, etc.)
 * @returns {Promise<Object>} Lista de vacunaciones
 */
export const getAllVaccinations = async (params = {}) => {
  try {
    const queryString = new URLSearchParams(params).toString();
    const url = `${BASE_URL}${API_VACCINATION_LIST}${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders()
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error al obtener vacunaciones:', error);
    throw error;
  }
};

/**
 * Obtener vacunación por ID
 * @param {string} id - ID de la vacunación
 * @returns {Promise<Object>} Datos de la vacunación
 */
export const getVaccinationById = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de vacunación es requerido');
    }

    const response = await fetch(`${BASE_URL}${API_VACCINATION_DETAILS}/${id}`, {
      method: 'GET',
      headers: getHeaders()
    });

    return await handleResponse(response);
  } catch (error) {
    console.error(`Error al obtener vacunación ${id}:`, error);
    throw error;
  }
};

/**
 * Crear nueva vacunación
 * @param {Object} vaccinationData - Datos de la vacunación
 * @returns {Promise<Object>} Vacunación creada
 */
export const createVaccination = async (vaccinationData) => {
  try {
    if (!vaccinationData) {
      throw new Error('Datos de vacunación son requeridos');
    }

    const response = await fetch(`${BASE_URL}${API_VACCINATION_CREATE}`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(vaccinationData)
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error al crear vacunación:', error);
    throw error;
  }
};

/**
 * Actualizar vacunación existente
 * @param {string} id - ID de la vacunación
 * @param {Object} vaccinationData - Datos actualizados
 * @returns {Promise<Object>} Vacunación actualizada
 */
export const updateVaccination = async (id, vaccinationData) => {
  try {
    if (!id) {
      throw new Error('ID de vacunación es requerido');
    }
    if (!vaccinationData) {
      throw new Error('Datos de vacunación son requeridos');
    }

    const response = await fetch(`${BASE_URL}${API_VACCINATION_UPDATE}/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(vaccinationData)
    });

    return await handleResponse(response);
  } catch (error) {
    console.error(`Error al actualizar vacunación ${id}:`, error);
    throw error;
  }
};

/**
 * Eliminar vacunación
 * @param {string} id - ID de la vacunación
 * @returns {Promise<Object>} Resultado de la eliminación
 */
export const deleteVaccination = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de vacunación es requerido');
    }

    const response = await fetch(`${BASE_URL}${API_VACCINATION_DELETE}/${id}`, {
      method: 'DELETE',
      headers: getHeaders()
    });

    return await handleResponse(response);
  } catch (error) {
    console.error(`Error al eliminar vacunación ${id}:`, error);
    throw error;
  }
};

// ========================================================================
// ⚡ OPERACIONES ESPECIALES
// ========================================================================

/**
 * Acciones en lote para múltiples vacunaciones
 * @param {Object} actionData - Datos de la acción en lote
 * @param {Array} actionData.ids - IDs de las vacunaciones
 * @param {string} actionData.action - Acción a realizar ('delete', 'update_status', etc.)
 * @param {Object} actionData.data - Datos adicionales según la acción
 * @returns {Promise<Object>} Resultado de la acción en lote
 */
export const bulkVaccinationAction = async (actionData) => {
  try {
    if (!actionData || !actionData.ids || !actionData.action) {
      throw new Error('Datos de acción en lote son requeridos (ids, action)');
    }

    const response = await fetch(`${BASE_URL}${API_VACCINATION_BULK_ACTION}`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(actionData)
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error en acción en lote:', error);
    throw error;
  }
};

/**
 * Enviar recordatorio de vacunación
 * @param {string} id - ID de la vacunación
 * @param {Object} reminderData - Datos adicionales del recordatorio
 * @returns {Promise<Object>} Resultado del envío
 */
export const sendVaccinationReminder = async (id, reminderData = {}) => {
  try {
    if (!id) {
      throw new Error('ID de vacunación es requerido');
    }

    const response = await fetch(`${BASE_URL}${API_VACCINATION_SEND_REMINDER}/${id}`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(reminderData)
    });

    return await handleResponse(response);
  } catch (error) {
    console.error(`Error al enviar recordatorio para vacunación ${id}:`, error);
    throw error;
  }
};

// ========================================================================
// 📊 FUNCIONES DE FILTRADO Y BÚSQUEDA
// ========================================================================

/**
 * Buscar vacunaciones por criterios específicos
 * @param {Object} searchCriteria - Criterios de búsqueda
 * @returns {Promise<Object>} Resultados de la búsqueda
 */
export const searchVaccinations = async (searchCriteria) => {
  try {
    const params = {
      search: searchCriteria.query || '',
      patientId: searchCriteria.patientId || '',
      vaccineId: searchCriteria.vaccineId || '',
      status: searchCriteria.status || '',
      dateFrom: searchCriteria.dateFrom || '',
      dateTo: searchCriteria.dateTo || '',
      page: searchCriteria.page || 1,
      limit: searchCriteria.limit || 10
    };

    // Remover parámetros vacíos
    Object.keys(params).forEach(key => {
      if (!params[key]) delete params[key];
    });

    return await getAllVaccinations(params);
  } catch (error) {
    console.error('Error en búsqueda de vacunaciones:', error);
    throw error;
  }
};

/**
 * Obtener vacunaciones por paciente
 * @param {string} patientId - ID del paciente
 * @returns {Promise<Object>} Vacunaciones del paciente
 */
export const getVaccinationsByPatient = async (patientId) => {
  try {
    if (!patientId) {
      throw new Error('ID del paciente es requerido');
    }

    return await getAllVaccinations({ patientId });
  } catch (error) {
    console.error(`Error al obtener vacunaciones del paciente ${patientId}:`, error);
    throw error;
  }
};

/**
 * Obtener vacunaciones por estado
 * @param {string} status - Estado de la vacunación ('Completed', 'Scheduled', 'Overdue')
 * @returns {Promise<Object>} Vacunaciones por estado
 */
export const getVaccinationsByStatus = async (status) => {
  try {
    if (!status) {
      throw new Error('Estado es requerido');
    }

    return await getAllVaccinations({ status });
  } catch (error) {
    console.error(`Error al obtener vacunaciones con estado ${status}:`, error);
    throw error;
  }
};

// ========================================================================
// 🔧 FUNCIONES AUXILIARES
// ========================================================================

/**
 * Validar datos de vacunación antes de enviar
 * @param {Object} vaccinationData - Datos a validar
 * @returns {Object} Resultado de la validación
 */
export const validateVaccinationData = (vaccinationData) => {
  const errors = [];
  const requiredFields = [
    'vaccineId', 'vaccineName', 'patientId', 'dateAdministered',
    'doseNumber', 'administrationRoute', 'administrationSite',
    'lotNumber', 'manufacturerId', 'expirationDate', 'administratorId'
  ];

  // Validar campos requeridos
  requiredFields.forEach(field => {
    if (!vaccinationData[field]) {
      errors.push(`${field} es requerido`);
    }
  });

  // Validar formato de fechas
  if (vaccinationData.dateAdministered && isNaN(Date.parse(vaccinationData.dateAdministered))) {
    errors.push('Fecha de administración debe ser válida');
  }

  if (vaccinationData.expirationDate && isNaN(Date.parse(vaccinationData.expirationDate))) {
    errors.push('Fecha de expiración debe ser válida');
  }

  // Validar número de dosis
  if (vaccinationData.doseNumber && (!Number.isInteger(Number(vaccinationData.doseNumber)) || Number(vaccinationData.doseNumber) < 1)) {
    errors.push('Número de dosis debe ser un entero positivo');
  }

  // Validar ruta de administración
  const validRoutes = ['Intramuscular', 'Subcutaneous', 'Oral', 'Intranasal', 'Intravenous'];
  if (vaccinationData.administrationRoute && !validRoutes.includes(vaccinationData.administrationRoute)) {
    errors.push('Ruta de administración no válida');
  }

  // Validar estado
  if (vaccinationData.status) {
    const validStatuses = ['Completed', 'Scheduled', 'Overdue'];
    if (!validStatuses.includes(vaccinationData.status)) {
      errors.push('Estado no válido');
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Formatear datos de vacunación para la API
 * @param {Object} vaccinationData - Datos a formatear
 * @returns {Object} Datos formateados
 */
export const formatVaccinationData = (vaccinationData) => {
  return {
    ...vaccinationData,
    doseNumber: Number(vaccinationData.doseNumber),
    dateAdministered: new Date(vaccinationData.dateAdministered).toISOString(),
    expirationDate: new Date(vaccinationData.expirationDate).toISOString(),
    status: vaccinationData.status || 'Scheduled'
  };
};

// ========================================================================
// 📤 EXPORTACIONES POR DEFECTO
// ========================================================================

const VaccinationService = {
  // CRUD básico
  getAllVaccinations,
  getVaccinationById,
  createVaccination,
  updateVaccination,
  deleteVaccination,
  
  // Operaciones especiales
  bulkVaccinationAction,
  sendVaccinationReminder,
  
  // Búsqueda y filtrado
  searchVaccinations,
  getVaccinationsByPatient,
  getVaccinationsByStatus,
  
  // Utilidades
  validateVaccinationData,
  formatVaccinationData
};

export default VaccinationService;